// 旧ビルダープレビュー scene/GameScene.tsx から復元・適応。
// builder の selectAllMapModel を直接ロードしてプレイ用 3D シーンを構築する。

/* eslint-disable react-hooks/exhaustive-deps */
import { useSelector } from "react-redux";
import { useRef, useMemo, useCallback } from "react";
import { Scene } from "@babylonjs/core/scene";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { RootState } from "app/store";
import { useScale } from "common/utils";
import * as action from "common/model/animations";
import { loadMesh, AssetModelType, MAP_MESH_SIZE } from "common/model";
import { useCanvas } from "features/courses/hook";
import { selectAllMapModel } from "features/builder/slice";
import { createOuterMesh } from "./util";

interface SceneRenderConfig {
  setLoading: (loading: boolean) => void;
  setScene: (scene: Scene) => void;
  setCharacter: (character: AbstractMesh) => void;
  setTotalGem: (totalGem: number) => void;
  handlePlusGemScore: (totalGem: number) => void;
  handleReachGoal: () => void;
}

export const GameScene = ({ config, reload }: { config: SceneRenderConfig; reload: boolean }) => {
  const { scale, ratio } = useScale();
  const elementRef = useRef<HTMLDivElement>(null);
  const mapModels = useSelector(selectAllMapModel);
  const size = useSelector((state: RootState) => state.builder.size);
  const radius = useMemo(
    () => (ratio > 1 ? (scale > 0.7 ? 20 / scale : 40 * scale) : 20 / scale),
    [scale, ratio],
  );

  const onSceneReady = useCallback(
    async (canvas: HTMLCanvasElement, engine: Engine, scene: Scene) => {
      const center = new Vector3((size.x * MAP_MESH_SIZE) / 2, 0, (size.z * MAP_MESH_SIZE) / 2);
      const camera = new ArcRotateCamera("camera", Math.PI / 4, Math.PI / 4, radius, center, scene);
      camera.lowerBetaLimit = 0;
      camera.upperBetaLimit = Math.PI / 2;
      camera.lowerRadiusLimit = radius - 10 / scale;
      camera.upperRadiusLimit = radius + 10 / scale;
      camera.attachControl(canvas, true);

      const light = new HemisphericLight("light", new Vector3(-3, 5, -3), scene);
      light.intensity = 1.5;

      scene.clearColor = new Color4(0, 0, 0, 0);

      const gems: AbstractMesh[] = [];
      let goal: AbstractMesh | undefined;
      let character: AbstractMesh | undefined;
      for (const model of mapModels) {
        const position = new Vector3(model.position.x, model.position.y, model.position.z);
        const isCharacter = model.type === AssetModelType.CHARACTER;
        // ピースの offset はビルダー編集画面（auto-lift あり）前提で調整された値のため、
        // キャラも編集画面と同じロード規約（isCharacter を渡さない = auto-lift あり）で読む。
        // コース流儀（auto-lift なし）で読むと offset の補正だけが効いてタイルに沈む。
        const mesh = await loadMesh(scene, model.filename, model.offset);
        mesh.getChildMeshes().forEach((subMesh) => {
          if (subMesh.name.indexOf("Cube") !== -1) {
            subMesh.isVisible = false;
          }
        });
        const boundingInfo = mesh.getBoundingInfo();
        const outer = createOuterMesh(scene, boundingInfo.boundingBox, position);
        outer.scaling = new Vector3(
          model.scaling?.x ?? 1,
          model.scaling?.y ?? 1,
          model.scaling?.z ?? 1,
        );
        outer.rotation = new Vector3(
          0,
          model.direction ? Math.atan2(Number(model.direction.x), Number(model.direction.z)) : 0,
          0,
        );
        mesh.parent = outer;

        if (isCharacter) {
          outer.name = AssetModelType.CHARACTER;
          character = outer;
          config.setCharacter(character);
          scene.getAnimationGroupByName("ArmLeftAction")?.stop();
          scene.getAnimationGroupByName("ArmRightAction")?.stop();
          scene.getAnimationGroupByName("BodyAction")?.play(true);
        } else if (model.type === AssetModelType.GEM) {
          outer.name = AssetModelType.GEM;
          gems.push(outer);
        } else if (model.type === AssetModelType.GOAL) {
          outer.name = AssetModelType.GOAL;
          goal = outer;
        } else if (model.type === AssetModelType.CONDITION) {
          outer.name = AssetModelType.CONDITION;
          outer.metadata = model.metadata;
        } else {
          outer.name = `${model.type}_asset`;
        }
      }

      const mark = await loadMesh(scene, "lightning.gltf.glb");
      mark.name = "mark";
      mark.setEnabled(false);

      gems.forEach((gem) => action.performAnimation(scene, gem));
      config.setTotalGem(gems.length);
      config.setLoading(false);

      scene.onAfterRenderObservable.add(() => {
        if (!scene.isDisposed) {
          gems.forEach((gem) => {
            if (!gem.isDisposed() && character && character.intersectsMesh(gem)) {
              gem.dispose();
              config.handlePlusGemScore(gems.length);
            }
          });
          // ゴール到達（クリア判定はアイテム取得状況と合わせて MainScene 側で行う）
          if (goal && !goal.isDisposed() && character && character.intersectsMesh(goal)) {
            config.handleReachGoal();
          }
        }
      });

      config.setScene(scene);

      engine.runRenderLoop(() => scene.render());
    },
    [],
  );

  useCanvas(
    elementRef,
    onSceneReady,
    (error) => {
      console.error(error);
    },
    reload,
  );

  return <div ref={elementRef} className="absolute w-full h-full pointer-events-none" />;
};
